import { faCalendarDay, faMoneyBill, faWaveSine } from "@fortawesome/pro-regular-svg-icons"
import { faBullhorn } from "@fortawesome/pro-solid-svg-icons"
import { translate } from "app/language/service"
import { IRootState, NOT_SAVED_ID } from "app/model"
import { getActiveAppContext } from "app/tools/common"
import { FORM_TAB_BOXES, IForm, IFormConfiguration } from "form/model"
import { getForm } from "form/tools/common"
import { onConfigurationFormChange } from "form/tools/configuration"
import { stopRefreshingFormikForm } from "form/tools/save"
import { Formik } from "formik"
import React from "react"
import { connect } from "react-redux"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"
import { MAX_TABLET_WIDTH_IN_PX } from "ui/model"
import { ConfigurationBudgetForm } from "./configuration/ConfigurationBudgetForm"
import { ConfigurationCapsForm } from "./configuration/ConfigurationCapsForm"
import { ConfigurationMainForm } from "./configuration/ConfigurationMainForm"
import { ConfigurationScheduleForm } from "./configuration/ConfigurationScheduleForm"
import { ContentBoxLayout } from "./layout/ContentBoxLayout"

interface IContentConfigurationProps {
  form: IForm | null
  isDesktop: boolean
}

const ContentConfigurationFn = React.memo(({ form, isDesktop }: IContentConfigurationProps) => {
  const [isRefreshing, setIsRefreshing] = React.useState(false)
  if (!form) {
    return null
  }
  React.useEffect(() => {
    if (!isRefreshing && form.context.refreshFormikForm) {
      setIsRefreshing(true)
      setTimeout(() => {
        stopRefreshingFormikForm()
        setIsRefreshing(false)
      }, 100)
    }
  })
  const readOnly = form.tempCampaignId === NOT_SAVED_ID
  const isForecastForm = form.context.isForecast
  const isDspDealForm = form.context.isDspDeal
  return (
    <FlexBox
      vertical
      ai="start"
      jc="start"
      fullWidth
      className="form"
      sx={{
        "pt": 0,
        "mt": 0,
        "& > *": {
          width: "100%"
        }
      }}>
      {isRefreshing ? (
        <LinearLoader id="form-is-refreshing" text={translate("form.refresh")} sx={{ mt: "2rem" }} />
      ) : (
        <Formik
          initialValues={form.configuration}
          validate={(configurationForm: IFormConfiguration) => onConfigurationFormChange(form, configurationForm)}
          onSubmit={(_values, actions) => actions.setSubmitting(false)}
          enableReinitialize>
          <>
            <ContentBoxLayout
              id={FORM_TAB_BOXES.CONFIGURATION_MAIN}
              title={translate(
                isForecastForm
                  ? "campaign.configuration.main.forecast.title"
                  : isDspDealForm
                    ? "campaign.configuration.main.dsp-deal.title"
                    : "campaign.configuration.main.campaign.title"
              )}
              subTitle={isForecastForm || isDspDealForm ? "" : translate("campaign.configuration.main.subtitle")}
              isDesktopScreen={isDesktop}
              icon={faBullhorn}>
              <ConfigurationMainForm form={form} />
            </ContentBoxLayout>
            <ContentBoxLayout
              id={FORM_TAB_BOXES.CONFIGURATION_BUDGET}
              title={translate("campaign.configuration.budget.title")}
              subTitle={isDspDealForm ? undefined : translate("campaign.configuration.budget.subtitle")}
              isDesktopScreen={isDesktop}
              icon={faMoneyBill}>
              <ConfigurationBudgetForm form={form} readOnly={readOnly} />
            </ContentBoxLayout>
            <ContentBoxLayout
              id={FORM_TAB_BOXES.CONFIGURATION_SCHEDULE}
              title={translate("campaign.configuration.schedule.title")}
              subTitle={translate("campaign.configuration.schedule.subtitle")}
              isDesktopScreen={isDesktop}
              icon={faCalendarDay}>
              <ConfigurationScheduleForm form={form} readOnly={readOnly} />
            </ContentBoxLayout>
            {!isDspDealForm && (
              <ContentBoxLayout
                id={FORM_TAB_BOXES.CONFIGURATION_CAPS}
                title={translate("campaign.configuration.caps.title")}
                subTitle={translate("campaign.configuration.caps.subtitle")}
                isDesktopScreen={isDesktop}
                icon={faWaveSine}>
                <ConfigurationCapsForm form={form} readOnly={false} />
              </ContentBoxLayout>
            )}
          </>
        </Formik>
      )}
    </FlexBox>
  )
})

export const ContentConfiguration = connect(
  (state: IRootState): IContentConfigurationProps => ({
    form: getActiveAppContext(state) ? getForm(state) : null,
    isDesktop: state.ui.container.width > MAX_TABLET_WIDTH_IN_PX
  })
)(ContentConfigurationFn)
